import { useEffect, useState } from 'react';
import Immutable from 'immutable';
import QuestionUtils from 'admin/utils/QuestionUtils';
import { EXAM_QUESTION_INPUT_TYPES } from 'edrolo/constants/QuestionTypes';
import { RectangleSelector } from 'react-image-annotation/lib/selectors';

// Transform
export const getDraggableAnnotations = (question) => {
  const questionInputFields = (
    question.get('fields') || Immutable.List()
  ).toJS();

  return questionInputFields.map((questionInputField) => {
    const geometry = {
      x: questionInputField.x_position,
      y: questionInputField.y_position,
      type: RectangleSelector.TYPE,
      width: questionInputField.width,
      height: questionInputField.height,
    };

    const id = questionInputField.id || Immutable.Map(geometry).hashCode();

    return {
      id,
      isDraggable: true,
      data: {
        ...questionInputField,
        id,
      },
      geometry,
    };
  });
};

export const getDropzoneAnnotations = (question) => {
  return (
    question
      .get('options')
      ?.toJS()
      .map((questionOption) => {
        const geometry = {
          x: questionOption.x_offset,
          y: questionOption.y_offset,
          type: RectangleSelector.TYPE,
          width: questionOption.width,
          height: questionOption.height,
        };
        const annotationId = Immutable.Map(geometry).hashCode();

        const targetAnnotationIds = question
          .get('fields')
          .toJS()
          .filter((questionInputField) =>
            questionInputField.correct_options?.includes(questionOption.id)
          )
          .map((targetInputField) => targetInputField.id);

        const id = questionOption.id || annotationId;

        return {
          id,
          isDraggable: false,
          targetAnnotationIds,
          data: {
            ...questionOption,
            correctAnswerValue: questionOption.value || '',
            id,
          },
          geometry,
        };
      }) || []
  );
};

// Remove
export const removeQuestionInputField = (
  question,
  replaceQuestion,
  annotation
) => {
  const { geometry } = annotation;

  const inputFields = question.get('fields');
  const targetInputField = inputFields.find(
    (inputField) =>
      inputField.get('x_position') === geometry.x &&
      inputField.get('y_position') === geometry.y
  );

  // Remove inputField
  const updatedInputFields = question
    .get('fields')
    .remove(
      question
        .get('fields')
        .findIndex((f) => f.get('id') === targetInputField.get('id'))
    );

  // Update the question
  const updatedQuestion = question.set('fields', updatedInputFields);
  replaceQuestion(updatedQuestion);
  return updatedQuestion;
};

export const removeQuestionOption = (question, replaceQuestion, annotation) => {
  const { data } = annotation;

  // Remove option
  const updatedOptions = question
    .get('options')
    .remove(question.get('options').findIndex((o) => o.get('id') === data.id));

  // Update the question
  const updatedQuestion = question.set('options', updatedOptions);
  replaceQuestion(updatedQuestion);
  return updatedQuestion;
};

export const removeAnnotation = (question, replaceQuestion, annotation) => {
  const { isDraggable } = annotation;

  if (isDraggable) {
    removeQuestionInputField(question, replaceQuestion, annotation);
  } else {
    removeQuestionOption(question, replaceQuestion, annotation);
  }
};

// Update
export const updateQuestionInputField = (
  question,
  replaceQuestion,
  annotation
) => {
  const { geometry, data } = annotation;

  const inputFields = question.get('fields');
  // TODO: May be just find by data.id given field and option annotations are separate for DnD?
  const targetInputField = inputFields
    .filter(
      (inputField) =>
        inputField.get('x_position') === geometry.x &&
        inputField.get('y_position') === geometry.y
    )
    .first();

  if (!targetInputField) {
    return question;
  }

  const updatedInputFields = question
    .get('fields')
    .setIn(
      [
        question
          .get('fields')
          .findIndex((f) => f.get('id') === targetInputField.get('id')),
        'label',
      ],
      data.label || ''
    );

  const updatedQuestion = question.set('fields', updatedInputFields);
  replaceQuestion(updatedQuestion);
  return updatedQuestion;
};

export const updateQuestionOption = (question, replaceQuestion, annotation) => {
  const { geometry, data } = annotation;

  const options = question.get('options');
  // TODO: May be just find by data.id given field and option annotations are separate for DnD?
  const targetOption = options
    .filter(
      (option) =>
        option.get('x_offset') === geometry.x &&
        option.get('y_offset') === geometry.y
    )
    .first();

  // FIXME: Throws an error when changing an annotation from draggable to dropzone
  // (targetOption is undefined)
  const updatedOptions = question
    .get('options')
    .setIn(
      [
        question
          .get('options')
          .findIndex((o) => o.get('id') === targetOption.get('id')),
        'value',
      ],
      data.correctAnswerValue || ''
    );

  const updatedQuestion = question.set('options', updatedOptions);
  replaceQuestion(updatedQuestion);
  return updatedQuestion;
};

export const linkOptionWithInputFields = (
  question,
  replaceQuestion,
  correctOptionId,
  targetInputFieldIds
) => {
  let updatedQuestion = question;

  updatedQuestion
    .get('fields')
    .toJS()
    .filter(
      (f) =>
        !targetInputFieldIds.includes(f.id) &&
        f.correct_options.includes(correctOptionId)
    )
    .forEach((field) => {
      const fieldToUpdateCorrectOptionsFor = updatedQuestion
        .get('fields')
        .filter((f) => f.get('id') === field.id)
        .first();

      const fieldWithUpdatedCorrectOptions = fieldToUpdateCorrectOptionsFor.set(
        'correct_options',
        fieldToUpdateCorrectOptionsFor
          .get('correct_options')
          .filter((c) => c !== correctOptionId)
      );

      const updatedInputFields = updatedQuestion
        .get('fields')
        .setIn(
          [
            updatedQuestion
              .get('fields')
              .findIndex(
                (f) => f.get('id') === fieldWithUpdatedCorrectOptions.get('id')
              ),
            'correct_options',
          ],
          fieldWithUpdatedCorrectOptions.get('correct_options')
        );

      updatedQuestion = updatedQuestion.set('fields', updatedInputFields);
    });

  targetInputFieldIds.forEach((targetInputFieldId) => {
    const updatedInputFields = updatedQuestion
      .get('fields')
      .setIn(
        [
          updatedQuestion
            .get('fields')
            .findIndex((f) => f.get('id') === targetInputFieldId),
          'correct_options',
        ],
        [correctOptionId]
      );

    updatedQuestion = updatedQuestion.set('fields', updatedInputFields);
  });

  replaceQuestion(updatedQuestion);
};

export const unlinkOptionWithInputFields = (
  question,
  replaceQuestion,
  correctOptionId
) => {
  let updatedQuestion = question;

  updatedQuestion
    .get('fields')
    .toJS()
    .filter((f) => f.correct_options.includes(correctOptionId))
    .forEach((field) => {
      const fieldToUpdateCorrectOptionsFor = updatedQuestion
        .get('fields')
        .filter((f) => f.get('id') === field.id)
        .first();

      const fieldWithUpdatedCorrectOptions = fieldToUpdateCorrectOptionsFor.set(
        'correct_options',
        fieldToUpdateCorrectOptionsFor
          .get('correct_options')
          .filter((c) => c !== correctOptionId)
      );

      const updatedInputFields = updatedQuestion
        .get('fields')
        .setIn(
          [
            updatedQuestion
              .get('fields')
              .findIndex(
                (f) => f.get('id') === fieldWithUpdatedCorrectOptions.get('id')
              ),
            'correct_options',
          ],
          fieldWithUpdatedCorrectOptions.get('correct_options')
        );

      updatedQuestion = updatedQuestion.set('fields', updatedInputFields);
    });

  replaceQuestion(updatedQuestion);
};

export const updateAnnotation = (question, replaceQuestion, annotation) => {
  const { targetAnnotationIds, id, isDraggable } = annotation;

  if (isDraggable) {
    updateQuestionInputField(question, replaceQuestion, annotation);
  } else {
    let updatedQuestion = updateQuestionOption(
      question,
      replaceQuestion,
      annotation
    );
    if (id && targetAnnotationIds?.length) {
      linkOptionWithInputFields(
        updatedQuestion,
        replaceQuestion,
        id,
        targetAnnotationIds
      );
    }
    if (id && !targetAnnotationIds?.length) {
      unlinkOptionWithInputFields(updatedQuestion, replaceQuestion, id);
    }
  }
};

// Add
export const addQuestionInputField = (
  question,
  replaceQuestion,
  annotation
) => {
  const { geometry, data } = annotation;
  const inputFields = question.get('fields') || Immutable.List();
  const newField = QuestionUtils.makeInputField({
    id: data.id,
    sort_order: inputFields.size,
    label: data.label || '',
    prefix: data.prefix || '',
    suffix: data.suffix || '',
    x_position: geometry.x,
    y_position: geometry.y,
    width: geometry.width,
    height: geometry.height,
    input_type: EXAM_QUESTION_INPUT_TYPES.CATEGORIZATION,
    // correct_options is set by linkOptionWithInputFields method during option create/update
  });
  const updatedQuestion = question.set('fields', inputFields.push(newField));
  replaceQuestion(updatedQuestion);
  return updatedQuestion;
};

export const addQuestionOption = (question, replaceQuestion, annotation) => {
  const { geometry, data } = annotation;
  const options = question.get('options') || Immutable.List();
  const newOption = QuestionUtils.makeQuestionOption({
    sort_order: options.size,
    value: data.correctAnswerValue || '',
    id: data.id,
    x_offset: geometry.x, // Using x_offset in lieu of x_position (options don't have it atm)
    y_offset: geometry.y, // Using y_offset in lieu of y_position (options don't have it atm)
    width: geometry.width,
    height: geometry.height,
  });

  const updatedQuestion = question.set('options', options.push(newOption));
  replaceQuestion(updatedQuestion);

  return updatedQuestion;
};

export const addAnnotation = (question, replaceQuestion, annotation) => {
  const { geometry, targetAnnotationIds, id, isDraggable } = annotation;
  const annotationId = id || Immutable.Map(geometry).hashCode();

  if (isDraggable) {
    addQuestionInputField(question, replaceQuestion, annotation);
  } else {
    const updatedQuestion = addQuestionOption(
      question,
      replaceQuestion,
      annotation
    );

    if (annotationId && targetAnnotationIds?.length) {
      const optionId = updatedQuestion
        .get('options')
        .find(
          (o) =>
            o.get('x_offset') === geometry.x && o.get('y_offset') === geometry.y
        )
        ?.get('id');

      linkOptionWithInputFields(
        updatedQuestion,
        replaceQuestion,
        optionId,
        targetAnnotationIds
      );
    }
  }
};

export const useDragAndDropAnnotationForm = (question, replaceQuestion) => {
  const [imageUrl, setImageUrl] = useState(question.get('image_url'));
  const [annotations, setAnnotations] = useState([
    ...getDraggableAnnotations(question),
    ...getDropzoneAnnotations(question),
  ]);

  useEffect(() => {
    setAnnotations([
      ...getDraggableAnnotations(question),
      ...getDropzoneAnnotations(question),
    ]);
  }, [question]);

  return {
    annotations,
    imageUrl,
    addAnnotation: (annotation) => {
      setAnnotations([...annotations, annotation]);
      addAnnotation(question, replaceQuestion, annotation);
    },
    updateAnnotation: (annotation) => {
      const otherAnnotations = annotations.filter(
        (a) => a.id !== annotation.id
      );
      setAnnotations([...otherAnnotations, { ...annotation }]);
      updateAnnotation(question, replaceQuestion, annotation);
    },
    removeAnnotation: (annotation) => {
      setAnnotations(
        annotations.filter((a) => a.data.id !== annotation.data.id)
      );
      removeAnnotation(question, replaceQuestion, annotation);
    },
    onImageUrlChange: (imageUrl) => {
      setImageUrl(imageUrl);
      replaceQuestion(
        question
          .set('fields', Immutable.List())
          .set('options', Immutable.List())
          .set('image_url', imageUrl)
      );
    },
  };
};
